import * as React from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Button, ErrorText, Field, Input, Spinner } from "@mandrac/ui";
import { PasswordField } from "../../components/fields";
import { api, ApiError } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import type { Me } from "../../lib/session";
import { AuthShell } from "./AuthShell";
import { homeFor, safeNext } from "./redirect";

type View = "login" | "forgot" | "forgotSent";

export function LoginPage() {
  const { t, te, locale } = useI18n();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const next = safeNext(params.get("next"));
  const [checking, setChecking] = React.useState(true);
  const [view, setView] = React.useState<View>("login");
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [devLink, setDevLink] = React.useState<string | undefined>();
  const sentMessage = React.useRef<HTMLParagraphElement>(null);

  // Already signed in (the sign-in is remembered on the device): skip the form.
  React.useEffect(() => {
    let alive = true;
    api<Me>("/auth/me").then(
      (me) => alive && navigate(next ?? homeFor(me), { replace: true }),
      () => alive && setChecking(false),
    );
    return () => {
      alive = false;
    };
  }, [navigate, next]);

  React.useEffect(() => {
    if (view === "forgotSent") sentMessage.current?.focus();
  }, [view]);

  async function login(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ home: "/admin" | "/dock" }>("/auth/login", { body: { email, password } });
      await qc.invalidateQueries({ queryKey: ["me"] });
      navigate(next ?? r.home, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "error.generic");
      setPassword("");
      setBusy(false);
    }
  }

  async function forgot(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ devLink?: string }>("/auth/password/forgot", { body: { email, locale } });
      setDevLink(r.devLink);
      setView("forgotSent");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "error.generic");
    } finally {
      setBusy(false);
    }
  }

  function show(v: View) {
    setError(null);
    setDevLink(undefined);
    setView(v);
  }

  if (checking) {
    return (
      <AuthShell title={t("login.title")}>
        <Spinner label={t("common.loading")} />
      </AuthShell>
    );
  }

  const emailField = (
    <Field label={t("login.email")}>
      {(p) => (
        <Input {...p} type="email" inputMode="email" autoComplete="username" autoCapitalize="none" spellCheck={false} required className="h-12" value={email} onChange={(e) => setEmail(e.target.value)} />
      )}
    </Field>
  );

  return (
    <AuthShell title={t("login.title")} subtitle={t("login.subtitle")}>
      {view === "login" ? (
        <form className="flex flex-col gap-4" onSubmit={login}>
          {emailField}
          <PasswordField label={t("login.password")} value={password} onChange={setPassword} autoComplete="current-password" required tall />
          {error ? <ErrorText>{te(error)}</ErrorText> : null}
          <Button type="submit" variant="accent" className="h-12 text-16" disabled={busy}>
            {t("login.submit")}
          </Button>
          <Button variant="link" className="min-h-11 self-start" onClick={() => show("forgot")}>
            {t("login.forgot")}
          </Button>
        </form>
      ) : view === "forgot" ? (
        <form className="flex flex-col gap-4" onSubmit={forgot}>
          <p className="m-0 text-15">{t("login.forgotIntro")}</p>
          {emailField}
          {error ? <ErrorText>{te(error)}</ErrorText> : null}
          <Button type="submit" variant="accent" className="h-12 text-16" disabled={busy}>
            {t("login.forgotSend")}
          </Button>
          <Button variant="link" className="min-h-11 self-start" onClick={() => show("login")}>
            {t("login.backToLogin")}
          </Button>
        </form>
      ) : (
        <div className="flex flex-col gap-4">
          <p ref={sentMessage} tabIndex={-1} role="status" className="m-0 text-15 outline-none">
            {t("login.forgotSent")}
          </p>
          {devLink ? (
            <a href={devLink} className="text-15 font-semibold" data-testid="dev-reset-link">
              {t("login.devLink")}
            </a>
          ) : null}
          <Button variant="link" className="min-h-11 self-start" onClick={() => show("login")}>
            {t("login.backToLogin")}
          </Button>
        </div>
      )}
    </AuthShell>
  );
}
